import {
    TransactionColumn,
    EntityCellVariant,
    EntityCellVariantSchema,
    ReviewColumnMetaSchema,
} from "@common/types/views/transactionViews";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { Entity } from "@common/types/entity";
import { getEntityPickerLock } from "@common/helpers/schedule-transaction-field-lock";
import {
    useIsReadonlyOrLocked,
    useIsTableReadonly,
} from "@hooks/useIsReadonlyOrLocked";
import { getTransactionsTableMeta } from "@hooks/view/useViewTableMeta";
import { useEntities, useEntitiesById } from "@hooks/useEntities";
import { Picker } from "@design-system/Picker/Picker.lib";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { TransactionReviewField } from "@common/types/transactionFieldReview";
import { addTransactionFieldReviews } from "@api/transaction.api";
import { useUser } from "@hooks/useUser";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import classNames from "classnames";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { PickerEntityItem } from "@components/entity/PickerEntityItem";
import { useChangeTransactionEntity } from "../useChangeTransactionEntity";
import { useChangeTransactionToPersonal } from "../useChangeTransactionToPersonal";
import { updateTransactionInCache } from "../useTransactionsQuery";
import { TransactionFieldReviewButton } from "./TransactionFieldReviewButton";
import { transactionCellAreEqual } from "./transactionCellComparators";
import {
    isTransactionFieldReviewedByUser,
    mergeTransactionFieldReviews,
} from "./transactionFieldReviewUtils";
import styles from "./EntityCell.module.scss";

export const EntityCell = memo(
    ({
        row,
        getValue,
        table,
        column,
    }: CellContext<ViewTableFeatures, TransactionListItem, Entity>) => {
        const entity = getValue();
        const user = useUser();
        const { error: errorToast } = useToaster();
        const isReadonlyOrLocked = useIsReadonlyOrLocked(row.original, table);
        const { isLocked: isEntityLocked, tooltip: lockTooltip } =
            getEntityPickerLock({
                schedule: row.original.schedule,
                isPeriodLocked: isReadonlyOrLocked,
            });
        const isTableReadonly = useIsTableReadonly(table);
        const { registerCellActionOpener } = getTransactionsTableMeta(table);
        const changeToBusiness = useChangeTransactionEntity(row.original);
        const changeToPersonal = useChangeTransactionToPersonal(row.original);
        const allEntities = useEntities({ excludeMockEntities: true });
        const entitiesById = useEntitiesById({ excludeMockEntities: true });
        const [isOpen, setIsOpen] = useState(false);

        const metaParse = ReviewColumnMetaSchema.safeParse(
            column.columnDef.meta,
        );
        const rawVariant =
            metaParse.data?.customVariants?.variant ??
            metaParse.data?.variant ??
            "full";
        const variantParse = EntityCellVariantSchema.safeParse(rawVariant);
        const variant: EntityCellVariant = variantParse.success
            ? variantParse.data
            : "full";
        const isReviewVariant = variant === "review";

        const isReviewed = useMemo(
            () =>
                isTransactionFieldReviewedByUser(
                    row.original.fieldReviews,
                    TransactionReviewField.ENTITY,
                    user?.id,
                ),
            [row.original.fieldReviews, user?.id],
        );

        const blurActiveElement = useCallback(() => {
            const activeElement = document.activeElement;
            if (activeElement instanceof HTMLElement) {
                activeElement.blur();
            }
        }, []);
        const closeAndBlur = useCallback(() => {
            setIsOpen(false);
            // Select restores focus to trigger on close; blur after that step.
            requestAnimationFrame(() => {
                blurActiveElement();
            });
        }, [blurActiveElement]);

        const handleEntityChange = useCallback(
            async (entityId: number | null) => {
                if (entityId === null) {
                    return;
                }
                if (entityId === entity?.id) {
                    // In review variant, re-selecting the current entity marks
                    // the field as reviewed even though nothing changed.
                    if (isReviewVariant) {
                        try {
                            const reviews = await addTransactionFieldReviews(
                                row.original.id,
                                [TransactionReviewField.ENTITY],
                            );
                            updateTransactionInCache(row.original.id, (t) => ({
                                ...t,
                                fieldReviews: mergeTransactionFieldReviews(
                                    t.fieldReviews,
                                    reviews,
                                ),
                            }));
                        } catch {
                            errorToast(
                                "Failed to mark entity as reviewed. Please try again.",
                            );
                        }
                    }
                    closeAndBlur();
                    return;
                }
                const selected = entitiesById.get(entityId);
                if (!selected) {
                    return;
                }
                const changeEntity = async () => {
                    if (selected.isPersonal) {
                        await changeToPersonal.mutateAsync();
                    } else {
                        await changeToBusiness.mutateAsync(selected);
                    }
                    // In review variant, automatically mark the entity field
                    // as reviewed after a successful change. The entity change
                    // has already committed, so surface a toast rather than
                    // letting a failed review mark fail silently.
                    if (isReviewVariant) {
                        try {
                            const reviews = await addTransactionFieldReviews(
                                row.original.id,
                                [TransactionReviewField.ENTITY],
                            );
                            updateTransactionInCache(row.original.id, (t) => ({
                                ...t,
                                fieldReviews: mergeTransactionFieldReviews(
                                    t.fieldReviews,
                                    reviews,
                                ),
                            }));
                        } catch {
                            errorToast(
                                "Failed to mark entity as reviewed. Please try again.",
                            );
                        }
                    }
                };
                await changeEntity().finally(() => {
                    closeAndBlur();
                });
            },
            [
                entity?.id,
                entitiesById,
                changeToBusiness,
                changeToPersonal,
                closeAndBlur,
                isReviewVariant,
                row.original.id,
                errorToast,
            ],
        );

        useEffect(() => {
            if (!registerCellActionOpener) {
                return;
            }

            return registerCellActionOpener({
                columnId: TransactionColumn.ENTITY,
                rowId: row.original.id,
                open: () => {
                    if (!isEntityLocked && allEntities.length > 1) {
                        setIsOpen(true);
                    }
                },
            });
        }, [
            registerCellActionOpener,
            row.original.id,
            isEntityLocked,
            allEntities.length,
        ]);

        const triggerLabel = entity?.name ?? "";

        const triggerContent = useMemo(
            () => (entity ? <EntityAvatar entity={entity} size="xs" /> : null),
            [entity],
        );

        const hasMultipleEntities = allEntities.length > 1;

        if (!hasMultipleEntities) {
            return (
                <ViewTableCell captureClicks className={styles.entityCell}>
                    {entity ? (
                        <EditableCellContent
                            truncationFullText={triggerLabel}
                            readonly
                            className={classNames({
                                [styles.reviewVariant]: isReviewVariant,
                                [styles.isReviewed]: isReviewed,
                            })}
                        >
                            {triggerContent}
                        </EditableCellContent>
                    ) : null}

                    {isReviewVariant && (
                        <TransactionFieldReviewButton
                            transaction={row.original}
                            isReadonly={isTableReadonly}
                            field={TransactionReviewField.ENTITY}
                            reviewTooltip="Review entity"
                        />
                    )}
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks className={styles.entityCell}>
                <Picker.Select
                    value={entity?.id ?? null}
                    onValueChange={handleEntityChange}
                    open={isOpen}
                    onOpenChange={setIsOpen}
                    chevron="none"
                    disabled={isEntityLocked}
                >
                    <Picker.CellContent
                        readOnly={isEntityLocked}
                        buttonProps={{ tooltip: lockTooltip }}
                        addText="Add entity"
                        className={classNames({
                            [styles.reviewVariant]: isReviewVariant,
                            [styles.isReviewed]: isReviewed,
                        })}
                        value={triggerContent ?? undefined}
                        aria-label={entity?.name ?? undefined}
                    />
                    <Picker.Popup>
                        <Picker.List>
                            {allEntities.map((e) => (
                                <PickerEntityItem
                                    key={e.id}
                                    value={e.id}
                                    entity={e}
                                />
                            ))}
                        </Picker.List>
                    </Picker.Popup>
                </Picker.Select>

                {isReviewVariant && (
                    <TransactionFieldReviewButton
                        transaction={row.original}
                        isReadonly={isTableReadonly}
                        field={TransactionReviewField.ENTITY}
                        reviewTooltip="Review entity"
                    />
                )}
            </ViewTableCell>
        );
    },
    transactionCellAreEqual,
);
EntityCell.displayName = "EntityCell";
